import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

const defaultIcon = L.icon({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
L.Marker.prototype.options.icon = defaultIcon;

interface GeoPoint {
  lat: number;
  lng: number;
  location_name: string | null;
}

interface GeoLabelCount {
  label: string;
  count: number;
}

interface GeographyData {
  summary: {
    total_users: number;
    with_location: number;
    without_location: number;
  };
  by_location_name: GeoLabelCount[];
  by_country: GeoLabelCount[];
  points: GeoPoint[];
  points_total: number;
  points_truncated: boolean;
}

const FitMapBounds: React.FC<{ points: GeoPoint[] }> = ({ points }) => {
  const map = useMap();

  useEffect(() => {
    if (points.length === 0) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number]));
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 12 });
  }, [points, map]);

  return null;
};

const CustomerGeography: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<GeographyData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [verifiedOnly, setVerifiedOnly] = useState(true);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin/analytics/customer-geography', {
        params: {
          verified_only: verifiedOnly ? '1' : '0',
          include_deleted: includeDeleted ? '1' : '0',
          active_only: activeOnly ? '1' : '0'
        }
      });
      if (response.data.success) {
        setData(response.data.data);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error loading customer geography:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [verifiedOnly, includeDeleted, activeOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const mapCenter = useMemo((): [number, number] => {
    if (data?.points?.length) {
      const lat =
        data.points.reduce((sum, p) => sum + p.lat, 0) / data.points.length;
      const lng =
        data.points.reduce((sum, p) => sum + p.lng, 0) / data.points.length;
      return [lat, lng];
    }
    return [20, 0];
  }, [data?.points]);

  const maxCityCount = data?.by_location_name[0]?.count ?? 1;
  const maxCountryCount = data?.by_country[0]?.count ?? 1;

  return (
    <>
      <div className="row mb-3">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h4 className="mb-0 fw-semibold" style={{ color: '#495057', fontSize: '1.25rem' }}>
                Customer geography
              </h4>
              <p className="text-muted mb-0 small">
                See where users are located to focus ads by city and country
              </p>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {lastUpdated && (
                <small className="text-muted">
                  Updated: {lastUpdated.toLocaleTimeString()}
                </small>
              )}
              <button type="button" className="btn btn-primary btn-sm" onClick={fetchData} disabled={loading}>
                <i className="bx bx-refresh me-1" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body py-2">
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <label className="d-flex align-items-center gap-1 small mb-0">
              <input
                type="checkbox"
                className="form-check-input"
                checked={verifiedOnly}
                onChange={(e) => setVerifiedOnly(e.target.checked)}
              />
              Verified users only
            </label>
            <label className="d-flex align-items-center gap-1 small mb-0">
              <input
                type="checkbox"
                className="form-check-input"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              Exclude banned
            </label>
            <label className="d-flex align-items-center gap-1 small mb-0">
              <input
                type="checkbox"
                className="form-check-input"
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(e.target.checked)}
              />
              Include deleted users
            </label>
          </div>
        </div>
      </div>

      {loading && !data ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : data ? (
        <>
          <div className="row mb-3 g-2">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body py-3">
                  <small className="text-muted">Total users (filters)</small>
                  <div className="fs-4 fw-semibold">{data.summary.total_users.toLocaleString()}</div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body py-3">
                  <small className="text-muted">With location on map</small>
                  <div className="fs-4 fw-semibold text-success">
                    {data.summary.with_location.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body py-3">
                  <small className="text-muted">No saved location</small>
                  <div className="fs-4 fw-semibold text-warning">
                    {data.summary.without_location.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body p-0">
              <div className="px-3 pt-3 pb-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h6 className="mb-0 text-muted fw-semibold">User locations</h6>
                <small className="text-muted">
                  Showing {data.points.length.toLocaleString()}
                  {data.points_truncated ? ` of ${data.points_total.toLocaleString()} (sampled)` : ''} points
                </small>
              </div>
              {data.points.length > 0 ? (
                <div style={{ height: 500, width: '100%' }}>
                  <MapContainer
                    center={mapCenter}
                    zoom={data.points.length === 1 ? 10 : 3}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <FitMapBounds points={data.points} />
                    <MarkerClusterGroup chunkedLoading>
                      {data.points.map((point, index) => (
                        <Marker
                          key={`${point.lat}-${point.lng}-${index}`}
                          position={[point.lat, point.lng]}
                        >
                          <Popup>
                            {point.location_name || 'Unknown area'}
                          </Popup>
                        </Marker>
                      ))}
                    </MarkerClusterGroup>
                  </MapContainer>
                </div>
              ) : (
                <div className="text-center text-muted py-5 px-3">
                  No users with valid coordinates match the current filters.
                </div>
              )}
            </div>
          </div>

          <div className="row g-3">
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <h6 className="text-muted fw-semibold mb-3">Top cities / areas</h6>
                  {data.by_location_name.length === 0 ? (
                    <p className="text-muted small mb-0">No data</p>
                  ) : (
                    <div className="table-responsive" style={{ maxHeight: 400, overflowY: 'auto' }}>
                      <table className="table table-sm table-hover mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>Location</th>
                            <th className="text-end">Users</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.by_location_name.map((row) => (
                            <tr key={row.label}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className="bg-primary rounded"
                                    style={{
                                      height: 8,
                                      width: `${Math.max(8, (row.count / maxCityCount) * 120)}px`,
                                      opacity: 0.7
                                    }}
                                  />
                                  <span className="small">{row.label}</span>
                                </div>
                              </td>
                              <td className="text-end fw-semibold">{row.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <h6 className="text-muted fw-semibold mb-3">Top countries</h6>
                  {data.by_country.length === 0 ? (
                    <p className="text-muted small mb-0">No data</p>
                  ) : (
                    <div className="table-responsive" style={{ maxHeight: 400, overflowY: 'auto' }}>
                      <table className="table table-sm table-hover mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>Country</th>
                            <th className="text-end">Users</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.by_country.map((row) => (
                            <tr key={row.label}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className="bg-success rounded"
                                    style={{
                                      height: 8,
                                      width: `${Math.max(8, (row.count / maxCountryCount) * 120)}px`,
                                      opacity: 0.7
                                    }}
                                  />
                                  <span className="small">{row.label}</span>
                                </div>
                              </td>
                              <td className="text-end fw-semibold">{row.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="alert alert-danger">Failed to load geography data.</div>
      )}
    </>
  );
};

export default CustomerGeography;
